import { ReactElement, useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../../config/api';

export default function ResetPassword(): ReactElement {
  const navigate = useNavigate();
  const { hash } = useParams<{ hash: string }>();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  //Estados para validação do hash
  const [isValidatingHash, setIsValidatingHash] = useState(true);
  const [isValidHash, setIsValidHash] = useState(false);

  //Validar hash ao carregar componente
  useEffect(() => {
    if (hash) {
      validateHash();
    } else {
      setError('Hash não fornecido');
      setIsValidatingHash(false);
    }
  }, [hash]);

  const validateHash = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/club/ValidarHash`, {
        params: { hash },
      });

      setIsValidHash(true);
    } catch (error: any) {
      console.error('Hash inválido:', error);
      setIsValidHash(false);
      setError('Link de recuperação inválido ou expirado');
    } finally {
      setIsValidatingHash(false);
    }
  };

  const passwordValidation = useMemo(() => {
    return {
      hasMinLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  }, [password]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    setError('');
    setMessage('');

    if (!isPasswordValid) {
      setError('A senha não atende aos requisitos mínimos de segurança.');
      return;
    }

    if (password !== confirm) {
      setError('As senhas não coincidem.');
      return;
    }

    try {
      setLoading(true);

      //URL e payload corretos
      const res = await axios.put(`${BASE_URL}/club/updateSenha`, {
        hash,
        novaSenha: password, //novaSenha em vez de password
      });

      if (res.status === 200) {
        setMessage('Senha redefinida com sucesso! Redirecionando...');
        setTimeout(() => navigate('/ClubeBeneficios'), 2500);
      }
    } catch (err: any) {
      console.error('Erro ao atualizar senha:', err);

      if (err.response) {
        console.error('Status:', err.response.status);
        console.error('Data:', err.response.data);
      }

      if (err.response?.status === 404) {
        setError('O link de recuperação é inválido ou expirou.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro ao redefinir senha. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // LOADING: Validando hash
  if (isValidatingHash) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-4 sm:px-6 bg-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Validando link de recuperação...
            </h2>
            <p className="text-gray-600">
              Aguarde enquanto verificamos seu link
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ERROR: Hash inválido
  if (!isValidHash) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-4 sm:px-6 bg-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4"></div>
            <h2 className="text-xl font-bold text-red-800 mb-2">
              Link Inválido
            </h2>
            <p className="text-red-700 mb-6">{error}</p>
            <button
              onClick={() => navigate('/ClubeBeneficios')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Voltar ao Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  //FORM: Hash válido, mostrar formulário
  return (
    <div className="w-full min-h-screen flex items-center justify-center px-4 sm:px-6 bg-gray-100">
      <div className="w-full max-w-5xl flex flex-col md:flex-row bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Painel Esquerdo */}
        <div className="hidden md:flex flex-col justify-center items-center bg-[#f3f6ff] p-0 md:w-1/2 relative">
          <div className="relative w-full h-[160px] overflow-hidden -translate-y-20">
            <svg
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              className="absolute top-0 left-0 w-full h-full rotate-180 fill-[rgb(86,27,133)]"
            >
              <path
                d="M 0 40 
                C 60 60 180 90 300 80 
                C 480 65 720 10 900 25 
                C 1040 40 1140 80 1200 60 
                V 120 H 0 Z"
              ></path>
            </svg>
          </div>
          <div>
            <div className="mb-8 mt-4">
              <img
                src="/assets/logos/ClubBeneficios.png"
                alt="Clube de Benefícios"
                className="w-[262px] h-[70px] object-contain"
              />
              <p className="text-center text-gray-600 mb-6 mt-4">
                Aproveite vantagens exclusivas para você!
              </p>
            </div>
          </div>
        </div>

        {/* Painel Direito */}
        <div className="flex flex-col justify-center items-center p-10 md:w-1/2 w-full">
          <h2 className="text-2xl font-bold text-blue-800 mb-2 text-center">
            Redefinir senha
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Digite sua nova senha abaixo.
          </p>

          {/*DEBUG: Informações do hash */}
          <div className="w-full max-w-sm mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs text-green-800 font-bold">Link válido!</p>
            <div className="text-xs text-green-700 mt-1">
              Hash: {hash?.substring(0, 15)}...
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="w-full max-w-sm space-y-4"
            autoComplete="off"
          >
            {/* Nova Senha */}
            <div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Nova senha"
                  className="w-full border border-gray-300 rounded-lg pl-4 pr-20 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>

              {/* Requisitos de senha */}
              <div className="mt-2 text-sm text-gray-700">
                <p className="mt-1 text-xs text-gray-500">
                  A senha deve conter pelo menos 6 caracteres, letras
                  maiúsculas, minúsculas, números e caracteres especiais.
                </p>

                <div className="flex flex-col mt-2 text-xs text-gray-700 bg-[beige] w-[30%]">
                  <label className="flex items-center gap-2">
                    {/* <input
                      type="checkbox"
                      checked={passwordValidation.hasMinLength}
                      readOnly
                      className="w-4 h-4"
                    /> */}
                    <span
                      className={
                        passwordValidation.hasMinLength
                          ? 'text-green-600'
                          : 'text-red-500'
                      }
                    >
                      Mín. 6 caracteres
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    {/* <input
                      type="checkbox"
                      checked={passwordValidation.hasUppercase}
                      readOnly
                      className="w-4 h-4"
                    /> */}
                    <span
                      className={
                        passwordValidation.hasUppercase
                          ? 'text-green-600'
                          : 'text-red-500'
                      }
                    >
                      Letra maiúscula
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    {/* <input
                      type="checkbox"
                      checked={passwordValidation.hasLowercase}
                      readOnly
                      className="w-4 h-4"
                    /> */}
                    <span
                      className={
                        passwordValidation.hasLowercase
                          ? 'text-green-600'
                          : 'text-red-500'
                      }
                    >
                      Letra minúscula
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={passwordValidation.hasNumber}
                      readOnly
                      className="w-4 h-4"
                    />
                    <span
                      className={
                        passwordValidation.hasNumber
                          ? 'text-green-600'
                          : 'text-red-500'
                      }
                    >
                      Números
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={passwordValidation.hasSpecialChar}
                      readOnly
                      className="w-4 h-4"
                    />
                    <span
                      className={
                        passwordValidation.hasSpecialChar
                          ? 'text-green-600'
                          : 'text-red-500'
                      }
                    >
                      Caractere especial
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Confirmar Senha */}
            <div className="relative">
              <div className="relative w-full">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirmar senha"
                  className="w-full border border-gray-300 rounded-lg pl-4 pr-20 py-3 focus:ring-2 focus:ring-blue-500 outline-none box-border"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 inset-y-0 text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap flex items-center"
                >
                  {showConfirm ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>

              {/* Indicador de confirmação de senha */}
              {confirm && (
                <div className="mt-2">
                  {password === confirm ? (
                    <p className="text-xs text-green-600">✓ Senhas coincidem</p>
                  ) : (
                    <p className="text-xs text-red-500">
                      ✗ Senhas não coincidem
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Mensagens */}
            {error && (
              <div className="text-red-600 bg-red-50 border border-red-200 p-2 rounded-lg text-sm text-center">
                {error}
              </div>
            )}
            {message && (
              <div className="text-green-600 bg-green-50 border border-green-200 p-2 rounded-lg text-sm text-center">
                {message}
              </div>
            )}

            {/* Botão */}
            <button
              type="submit"
              disabled={loading || !isPasswordValid || password !== confirm}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Redefinindo...' : 'Redefinir senha'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/ClubeBeneficios')}
              className="w-full text-blue-600 text-sm text-center mt-2 hover:underline"
            >
              Voltar ao login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
